import React, { useEffect, useId, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Spinner from 'react-bootstrap/Spinner';
import Tooltip from 'react-bootstrap/Tooltip';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import YearValuesModal from './YearValuesModal';
import MonthlyValuesModal from './MonthlyValuesModal';
import {
    DESCRIPTION_MAX, FORECAST_YEARS, ForecastMethod, METHOD_FIELDS, METHOD_LABELS, MethodField, baseIncomeLines, cashItemKey, fieldApplies, fitsDecimals, fromParams,
    isYearsField, linkableIncomeLines, monthOptions, numKeyDown, toParams,
} from './forecastUtils';

interface Props {
    category: any;            // tab from GET /forecasts/categories
    forecast: any;
    editing?: any;            // line being edited, or undefined for a new line
    readOnly: boolean;
    saving: boolean;
    savedMessage?: string;    // 'added' | 'updated' after a successful save
    resetToken?: number;      // bumped by the parent on each save -> clear all inputs
    onSubmit: (body: any) => void;
    onCancelEdit: () => void;
    onClear?: () => void;
}

// react-select styled like the click-through's editable (cream) inputs;
// grayed out when the form is read-only so it doesn't look editable.
const selectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        background: state.isDisabled ? '#e9ecef' : '#fff8e7',
        borderColor: '#dce2e8',
        minHeight: 42,
        borderRadius: 6,
        cursor: state.isDisabled ? 'not-allowed' : 'default',
    }),
    singleValue: (base: any, state: any) => ({ ...base, color: state.isDisabled ? '#667085' : base.color }),
    menu: (base: any) => ({ ...base, zIndex: 20 }),
};

/** Circled "i" next to a label; hover or keyboard focus shows a short guidance tip. */
const Tip: React.FC<{ text: React.ReactNode }> = ({ text }) => {
    const tipId = useId();
    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={tipId}>{text}</Tooltip>}>
            <span className="fyf-tip" tabIndex={0} aria-label={typeof text === 'string' ? text : undefined}>i</span>
        </OverlayTrigger>
    );
};

// Assumption values cap at 16 digits (same bound used for starting cash);
// percent fields cap at 4 digits - a rate beyond 9999% is never intended.
const MAX_ASSUMPTION = 1e16;
const MAX_PERCENT = 1e4;
const isPercentKind = (kind: string): boolean => kind.includes('percent');
const fitsField = (f: MethodField, v: any): boolean =>
    fitsDecimals(v) &&
    (v === '' || v === undefined || v === null ||
    (Number.isFinite(Number(v)) && Math.abs(Number(v)) < (isPercentKind(f.kind) ? MAX_PERCENT : MAX_ASSUMPTION)));

/**
 * Add / edit one forecast line. Fields change with the selected method, so only
 * the inputs that method uses are shown.
 */
const LineForm: React.FC<Props> = ({ category, forecast, editing, readOnly, saving, savedMessage, resetToken, onSubmit, onCancelEdit, onClear }) => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const isCash = category.key === 'cash_financing';
    // Expenses that scale with staff (cell phone, per-seat tools) also offer Per Employee,
    // inserted after Fixed Monthly when the API's method list doesn't include it yet.
    const methods: ForecastMethod[] = useMemo(() => {
        const m: ForecastMethod[] = category.methods || [];
        if (category.key !== 'operating_expense' || m.includes('per_employee')) return m;
        const i = m.indexOf('fixed_monthly');
        return [...m.slice(0, i + 1), 'per_employee' as ForecastMethod, ...m.slice(i + 1)];
    }, [category]);

    const [accountId, setAccountId] = useState<number | null>(null);
    const [customMode, setCustomMode] = useState<boolean>(false);
    const [customName, setCustomName] = useState<string>('');
    const [cashItem, setCashItem] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [method, setMethod] = useState<ForecastMethod>(methods[0]);
    const [startMonth, setStartMonth] = useState<number>(1);
    const [endMonth, setEndMonth] = useState<number | ''>('');
    const [fields, setFields] = useState<Record<string, any>>(fromParams(methods[0]));
    const [error, setError] = useState<string>('');
    const [yearsField, setYearsField] = useState<MethodField | null>(null);
    // Direct Monthly Entry: monthly values typed in the pop-up before the line is saved.
    const [monthlyDraft, setMonthlyDraft] = useState<{ month_index: number; amount: number | null }[]>([]);
    const [showMonthly, setShowMonthly] = useState<boolean>(false);

    const reset = () => {
        setAccountId(null);
        setCustomMode(false);
        setCustomName('');
        setCashItem(category.items?.[0]?.key || '');
        setDescription('');
        setMethod(methods[0]);
        setStartMonth(1);
        setEndMonth('');
        setFields(fromParams(methods[0]));
        setError('');
        setMonthlyDraft([]);
        setShowMonthly(false);
    };

    useEffect(() => {
        if (editing) {
            setAccountId(editing.master_chart_of_account_id || null);
            setCustomMode(!editing.master_chart_of_account_id && !isCash);
            setCustomName(editing.custom_account_name || '');
            setCashItem(cashItemKey(editing.params?.item) || category.items?.[0]?.key || '');
            setDescription(editing.description || '');
            setMethod(editing.method);
            setStartMonth(editing.start_month_index || 1);
            setEndMonth(editing.end_month_index || '');
            setFields(fromParams(editing.method, editing.params));
            setError('');
            setMonthlyDraft((editing.monthly_values || []).map((mv: any) => ({ month_index: mv.month_index, amount: mv.amount })));
        } else {
            reset();
        }
    }, [editing, category.key, resetToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // Accounts grouped by COA sub-header for the dropdown.
    const accountOptions = useMemo(() => {
        const groups: Record<string, any[]> = {};
        (category.accounts || []).forEach((a: any) => {
            const g = a.group || category.header_name || category.label;
            (groups[g] = groups[g] || []).push({ value: a.id, label: `${a.account ?? ''} — ${a.name}` });
        });
        return Object.keys(groups).map((g) => ({ label: g, options: groups[g] }));
    }, [category]);
    const flatAccounts = useMemo(() => accountOptions.flatMap((g: any) => g.options), [accountOptions]);
    const months = useMemo(() => monthOptions(forecast.start_month, forecast.start_year), [forecast]);
    const incomeLines = useMemo(() => linkableIncomeLines(forecast.lines), [forecast]);
    const baseLines = useMemo(() => baseIncomeLines(forecast.lines, editing?.id), [forecast, editing]);

    const changeMethod = (m: ForecastMethod) => {
        setMethod(m);
        setFields(fromParams(m, toParams(method, fields)));
        setError('');
        // Direct Monthly lines ARE their monthly entries - open the pop-up at once.
        if (m === 'direct_monthly') setShowMonthly(true);
    };

    // Field-level filter: the method's own `when` rule plus the category scope (`cats`).
    const applies = (f: MethodField) => fieldApplies(f, fields) && (!f.cats || f.cats.includes(category.key));

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        const body: any = {
            description: description.trim() || null,
            method,
            start_month_index: startMonth,
            end_month_index: endMonth === '' ? null : endMonth,
            params: toParams(method, fields),
        };
        // Units typed in the Monthly Values pop-up are not form fields: keep them.
        if (editing && method === 'units_price' && editing.params?.units_by_month) {
            body.params.units_by_month = editing.params.units_by_month;
        }
        if (endMonth !== '' && endMonth < startMonth) {
            return setError(t('entry.endBeforeStart', 'The end month must be on or after the start month.'));
        }
        // Required assumption fields must be filled. income_line/base_line are
        // covered by their own checks below; Units is waived when the edited
        // line's units come from typed monthly values instead of the base field.
        const reqField = METHOD_FIELDS[method].filter(applies).find((f) => {
            if (!f.required || f.kind === 'income_line' || f.kind === 'base_line') return false;
            if (f.key === 'units' && Object.keys(editing?.params?.units_by_month || {}).length > 0) return false;
            return isYearsField(f.kind)
                ? yearsOf(f.key).every((v) => v === '' || v === undefined || v === null)
                : fields[f.key] === '' || fields[f.key] === undefined || fields[f.key] === null;
        });
        if (reqField) {
            return setError(String(t('entry.fieldRequired', '{{field}} is required.',
                { field: t(reqField.i18nKey || `field.${reqField.key}`, reqField.label) })));
        }
        // Quantities, prices, amounts and rates cannot be negative — including
        // every typed year of a *_years field.
        const neg = (v: any) => v !== '' && v !== undefined && v !== null && !Number.isNaN(Number(v)) && Number(v) < 0;
        const negField = METHOD_FIELDS[method].filter(applies)
            .find((f) => isYearsField(f.kind)
                ? yearsOf(f.key).some(neg)
                : ['number', 'currency', 'percent'].includes(f.kind) && neg(fields[f.key]));
        if (negField) {
            return setError(String(t('entry.noNegative', '{{field}} cannot be negative.',
                { field: t(negField.i18nKey || `field.${negField.key}`, negField.label) })));
        }
        // Assumption values cannot exceed their digit bound (16 digits, or 4
        // for percent fields) - including every typed year of a *_years field.
        const bigField = METHOD_FIELDS[method].filter(applies)
            .find((f) => isYearsField(f.kind)
                ? yearsOf(f.key).some((v) => !fitsField(f, v))
                : ['number', 'currency', 'percent'].includes(f.kind) && !fitsField(f, fields[f.key]));
        if (bigField) {
            const fieldLabel = String(t(bigField.i18nKey || `field.${bigField.key}`, bigField.label));
            return setError(String(isPercentKind(bigField.kind)
                ? t('entry.tooLargePercent', '{{field}} must be 4 digits or fewer.', { field: fieldLabel })
                : t('entry.tooLarge', '{{field}} must be 16 digits or fewer.', { field: fieldLabel })));
        }
        if (isCash) {
            const item = category.items.find((i: any) => i.key === cashItem);
            if (!item) return setError(t('entry.pickItem', 'Select a cash & financing item.'));
            body.custom_account_name = item.name;
            body.params.item = item.key;
            body.params.direction = item.direction;
        } else if (customMode) {
            if (!customName.trim()) return setError(t('entry.customRequired', 'Enter a category name.'));
            body.custom_account_name = customName.trim();
            if (editing) body.master_chart_of_account_id = null;
        } else {
            if (!accountId) return setError(t('entry.accountRequired', 'Select an account.'));
            body.master_chart_of_account_id = accountId;
        }
        if (method === 'linked_units' && !body.params.income_line_id) {
            return setError(t('entry.incomeLineRequired', 'Select the income product this cost follows.'));
        }
        if (method === 'percent_income' && body.params.base === 'income_line' && !body.params.income_line_id) {
            return setError(t('entry.baseLineRequired', 'Select the income line this percentage is based on.'));
        }
        // Direct Monthly values typed in the pop-up travel with the line. For an
        // edited line, months that were cleared are sent as null so the API removes them.
        if (method === 'direct_monthly') {
            const vals: any[] = [...monthlyDraft];
            (editing?.monthly_values || []).forEach((mv: any) => {
                if (!vals.some((v) => v.month_index === mv.month_index)) vals.push({ month_index: mv.month_index, amount: null });
            });
            if (vals.length) body.monthly_values = vals;
        }
        if (!editing) body.category = category.key;
        onSubmit(body);
    };

    // Any user edit clears the last validation message - a fixed field no longer shows its error.
    const setField = (key: string, value: any) => { setFields((f) => ({ ...f, [key]: value })); setError(''); };

    // Years fields: the form shows Year 1; Years 2-5 are edited in the YearValuesModal pop-up.
    const yearsOf = (key: string): any[] => {
        const arr: any[] = Array.isArray(fields[key]) ? fields[key] : [fields[key] ?? ''];
        return Array.from({ length: FORECAST_YEARS }, (_, i) => arr[i] ?? '');
    };
    const laterYearsText = (f: MethodField): string => {
        const arr = yearsOf(f.key);
        const unit = f.kind === 'percent_years' ? '%' : '';
        const cur = f.kind === 'currency_years' ? '$' : '';
        const parts = arr.slice(1).map((v, i) => (v === '' ? '' : `Y${i + 2}: ${cur}${Number(v).toLocaleString()}${unit}`))
            .filter((x) => x);
        return parts.length ? parts.join(' · ') : '';
    };

    const editCls = 'fyf-edit';

    // A line-shaped object for the Monthly Values pop-up while the line is unsaved.
    // Frozen while the pop-up is open so typing isn't reset by re-renders.
    const monthlyLine = useMemo(() => ({
        method: 'direct_monthly',
        account_name: editing?.account_name
            || (customMode ? customName : '')
            || (flatAccounts.find((o: any) => o.value === accountId)?.label || '').split('—').pop()!.trim()
            || (isCash ? (category.items?.find((i: any) => i.key === cashItem)?.name || '') : '')
            || String(t('entry.newAccount', 'New account')),
        description,
        start_month_index: startMonth,
        monthly_values: monthlyDraft,
        params: {},
    }), [showMonthly]); // eslint-disable-line react-hooks/exhaustive-deps

    const mergeMonthlyDraft = (vals: { month_index: number; amount: number | null }[]) => {
        setMonthlyDraft((d) => {
            const map = new Map<number, number | null>(d.map((v) => [v.month_index, v.amount]));
            vals.forEach((v) => (v.amount === null ? map.delete(v.month_index) : map.set(v.month_index, v.amount)));
            return Array.from(map, ([month_index, amount]) => ({ month_index, amount })).sort((a, b) => a.month_index - b.month_index);
        });
        setShowMonthly(false);
    };

    return (
        <Form onSubmit={submit} className="fyf-panel" noValidate>
            <h3>{editing ? t('entry.editLine', 'Edit Forecast Account') : t('entry.addLine', 'Add Forecast Account')}</h3>
            <fieldset disabled={readOnly}>
                {/* One 3-column grid: every box lines up on the same column edges. */}
                <div className="fyf-form3">
                    <div className="fyf-span2">
                        {isCash ? (
                            <>
                                <Form.Label>{t('entry.item', 'Cash & Financing Item')}<span className="text-danger"> *</span>
                                    <Tip text={t('tip.item', 'Pick the type of cash activity this line represents')} /></Form.Label>
                                <Form.Select className={editCls} value={cashItem} onChange={(e) => { setCashItem(e.target.value); setError(''); }}>
                                    {(['in', 'out'] as const).map((dir) => (
                                        <optgroup key={dir} label={dir === 'in' ? t('cash.cashIn', 'Cash In') : t('cash.cashOut', 'Cash Out')}>
                                            {category.items.filter((i: any) => i.direction === dir)
                                                .map((i: any) => <option key={i.key} value={i.key}>{i.name}</option>)}
                                        </optgroup>
                                    ))}
                                </Form.Select>
                            </>
                        ) : customMode ? (
                            <>
                                <Form.Label>{t('entry.customName', 'Forecast-only category')}<span className="text-danger"> *</span>
                                    <Tip text={t('tip.customName', 'Name this forecast-only line; it is not posted to the GL')} /></Form.Label>
                                <Form.Control className={editCls} value={customName} maxLength={255} onChange={(e) => { setCustomName(e.target.value); setError(''); }}
                                    placeholder={t('entry.customPlaceholder', 'e.g. Network Partner Payments')} />
                                <div className="fyf-counter">{customName.length}/255</div>
                            </>
                        ) : (
                            <>
                                <Form.Label>{t('entry.account', 'Available GL Account')}<span className="text-danger"> *</span>
                                    <Tip text={t('tip.account', 'GL account to forecast, or use the link below for a forecast-only name')} /></Form.Label>
                                <Select
                                    styles={selectStyles}
                                    options={accountOptions}
                                    value={flatAccounts.find((o: any) => o.value === accountId) || null}
                                    onChange={(o: any) => { setAccountId(o ? o.value : null); setError(''); }}
                                    placeholder={t('entry.selectAccount', 'Select an account…')}
                                    isDisabled={readOnly}
                                    isClearable
                                    noOptionsMessage={() => t('entry.noAccounts', 'No accounts in this category')}
                                />
                            </>
                        )}
                        {!isCash && (
                            <button type="button" className="fyf-link" onClick={() => setCustomMode((c) => !c)}>
                                {customMode
                                    ? t('entry.useCoa', 'Pick from the Chart of Accounts instead')
                                    : t('entry.useCustom', 'Account not listed? Add a forecast-only category')}
                            </button>
                        )}
                    </div>
                    <div>
                        <Form.Label>{t('entry.method', 'Forecast Method')}
                            <Tip text={t('tip.method', 'How this line is calculated; the inputs below change with it')} /></Form.Label>
                        <Form.Select className={editCls} value={method} onChange={(e) => changeMethod(e.target.value as ForecastMethod)}>
                            {methods.map((m) => <option key={m} value={m}>{t(`method.${m}`, METHOD_LABELS[m])}</option>)}
                        </Form.Select>
                    </div>
                    <div className="fyf-row3">
                        <div>
                            <Form.Label htmlFor="fyf-desc">{t('entry.description', 'Product / Description')}</Form.Label>
                            <Form.Control id="fyf-desc" className={editCls} value={description} maxLength={DESCRIPTION_MAX}
                                onChange={(e) => { setDescription(e.target.value.slice(0, DESCRIPTION_MAX)); setError(''); }} />
                            <div className="fyf-counter">{description.length}/{DESCRIPTION_MAX}</div>
                        </div>
                        <div>
                            <Form.Label htmlFor="fyf-start">{t('entry.startMonth', 'Start Month')}</Form.Label>
                            <Form.Select id="fyf-start" className={editCls} value={startMonth} onChange={(e) => { setStartMonth(Number(e.target.value)); setError(''); }}>
                                {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </Form.Select>
                        </div>
                        <div>
                            <Form.Label htmlFor="fyf-end">{t('entry.endMonth', 'End Month (optional)')}</Form.Label>
                            <Form.Select id="fyf-end" className={editCls} value={endMonth}
                                onChange={(e) => { setEndMonth(e.target.value === '' ? '' : Number(e.target.value)); setError(''); }}>
                                <option value="">{t('entry.noEndMonth', 'No end (runs to the end of the forecast)')}</option>
                                {months.filter((m) => m.value >= startMonth)
                                    .map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </Form.Select>
                        </div>
                    </div>
                    {METHOD_FIELDS[method].some(applies) && (
                        <div className="fyf-subhead">{t('entry.assumptions', 'Forecast assumptions')}</div>
                    )}
                    <div className="fyf-fields3">
                        {METHOD_FIELDS[method].filter(applies).map((f) => (
                            <div key={f.key}>
                                <Form.Label>
                                    {t(f.i18nKey || `field.${f.key}`, f.label)}
                                    {f.required && <span className="text-danger"> *</span>}
                                    {f.tip && <Tip text={t(`tip.${method}.${f.key}`, f.tip)} />}
                                </Form.Label>
                                {isYearsField(f.kind) ? (
                                    <>
                                        <div className="input-group">
                                            {f.kind === 'currency_years' && <span className="input-group-text">$</span>}
                                            <Form.Control className={editCls} type="number" step="any" min={0}
                                                aria-label={String(t(f.i18nKey || `field.${f.key}`, f.label))}
                                                onKeyDown={numKeyDown}
                                                value={yearsOf(f.key)[0] ?? ''}
                                                onChange={(e) => { if (fitsField(f, e.target.value)) setField(f.key, yearsOf(f.key).map((x, k) => (k === 0 ? e.target.value : x))); }} />
                                            {f.kind === 'percent_years' && <span className="input-group-text">%</span>}
                                        </div>
                                        <div className="fyf-byyear">
                                            {laterYearsText(f) && <span className="fyf-byyear-sum">{laterYearsText(f)}</span>}
                                            {!readOnly && (
                                                <button type="button" className="fyf-link" onClick={() => setYearsField(f)}>
                                                    {laterYearsText(f)
                                                        ? t('entry.editByYear', 'Edit values by year')
                                                        : t('entry.differentByYear', 'Different value for later years?')}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                ) : f.kind === 'income_line' ? (
                                    incomeLines.length ? (
                                        <Form.Select className={editCls} value={fields[f.key] ?? ''} onChange={(e) => setField(f.key, e.target.value)}>
                                            <option value="">{t('entry.selectIncomeLine', 'Select an income product…')}</option>
                                            {incomeLines.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </Form.Select>
                                    ) : (
                                        <div className="fyf-note mt-0">
                                            {t('entry.noIncomeLines', 'Add a Units × Price or Subscription line on the Gross Operating Income tab first.')}
                                        </div>
                                    )
                                ) : f.kind === 'base_line' ? (
                                    baseLines.length ? (
                                        <Form.Select className={editCls} value={fields[f.key] ?? ''} onChange={(e) => setField(f.key, e.target.value)}>
                                            <option value="">{t('entry.selectBaseLine', 'Select an income line…')}</option>
                                            {baseLines.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </Form.Select>
                                    ) : (
                                        <div className="fyf-note mt-0">
                                            {t('entry.noBaseLines', 'Add an income line on the Gross Operating Income tab first.')}
                                        </div>
                                    )
                                ) : f.kind === 'select' ? (
                                    <Form.Select className={editCls} value={fields[f.key] ?? f.defaultValue} onChange={(e) => setField(f.key, e.target.value)}>
                                        {f.options!.map((o) => <option key={o.value} value={o.value}>{t(`field.${f.key}.${o.value}`, o.label)}</option>)}
                                    </Form.Select>
                                ) : (
                                    <div className="input-group">
                                        {f.kind === 'currency' && <span className="input-group-text">$</span>}
                                        <Form.Control className={editCls} type="number" step="any" min={0} value={fields[f.key] ?? ''}
                                            aria-label={String(t(f.i18nKey || `field.${f.key}`, f.label))}
                                            onKeyDown={numKeyDown}
                                            onChange={(e) => { if (fitsField(f, e.target.value)) setField(f.key, e.target.value); }} />
                                        {f.kind === 'percent' && <span className="input-group-text">%</span>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {method === 'direct_monthly' && (
                    <div className="fyf-note">
                        {t('entry.directHint', 'Monthly entry is used when the account should not be driven by a formula. After adding, enter each month under "Monthly values". Expense and deduction amounts are entered as positive numbers; the forecast engine applies the correct add/subtract logic.')}{' '}
                        <button type="button" className="fyf-link" onClick={() => setShowMonthly(true)}>
                            {t('entry.monthly', 'Monthly values')}{monthlyDraft.length ? ` (${monthlyDraft.length})` : ''}
                        </button>
                    </div>
                )}
                {error && <div className="fyf-error">{error}</div>}

                <div className="fyf-actions">
                    <button type="submit" className="fyf-primary" disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" />
                            : editing ? t('entry.saveLine', 'Save Changes') : t('entry.add', 'Add to Forecast')}
                    </button>
                    <button type="button" className="fyf-secondary" onClick={editing ? onCancelEdit : () => { reset(); onClear && onClear(); }}>
                        {editing ? t('common.cancel', 'Cancel') : t('entry.clear', 'Clear Entry')}
                    </button>
                    {methods.includes('direct_monthly') && method !== 'direct_monthly' && (
                        <button type="button" className="fyf-secondary" onClick={() => changeMethod('direct_monthly')}>
                            {t('method.direct_monthly', 'Direct Monthly Entry')}
                        </button>
                    )}
                </div>
            </fieldset>

            <MonthlyValuesModal
                show={showMonthly}
                line={monthlyLine}
                forecast={forecast}
                readOnly={readOnly}
                saving={false}
                onClose={() => setShowMonthly(false)}
                onSave={(vals) => mergeMonthlyDraft(vals)}
            />

            <YearValuesModal
                show={!!yearsField}
                label={yearsField ? String(t(yearsField.i18nKey || `field.${yearsField.key}`, yearsField.label)) : ''}
                kind={yearsField?.kind || 'number_years'}
                noCarry={yearsField?.noCarry}
                values={yearsField ? yearsOf(yearsField.key) : []}
                onClose={() => setYearsField(null)}
                onSave={(vals) => { if (yearsField) setField(yearsField.key, vals); setYearsField(null); }}
            />

            {savedMessage && !editing && (
                <div className="fyf-saved">
                    <b>{savedMessage === 'updated' ? t('entry.savedUpdated', 'Account updated.') : t('entry.savedAdded', 'Account added to forecast.')}</b>{' '}
                    {t('entry.savedHint', 'Continue with another account or move to the next category.')}
                </div>
            )}
        </Form>
    );
};

export default LineForm;
